/**
 * Sample item for the Item Detail third-pass Lab page. Every value is example
 * content, not workspace data. Mirrors the mockup published 2026-10-14
 * (claude.ai/artifact/Dxsg9MdYXNp6Ma5HPNBUr1).
 */

import type { AssessmentRow, AttentionLine, CycleNode, LinkRow } from './itemDetailTypes';

// The item page's row types live with the product page (./itemDetailTypes.ts);
// re-exported here so this sample's old import path still works.
export type { AssessmentRow, AttentionLine, CycleNode, Destination, LinkRow, SessionNode, SettingSource, TrackNode } from './itemDetailTypes';

export const THIRD_PASS = {
  title: 'Alternate Valve Supplier Qualification (dual-source)',
  description:
    'Qualify a second dispense-valve supplier with the corrected seat elastomer. Executes the July dual-source decision: first-article inspection, material certs, 500-hour syrup-exposure life test, and PPAP.',
  owner: 'Ray Delgado',
  version: 4,
  rankedVersion: 4,
  groupsOnRankedVersion: 2,
  standing: [
    { label: 'Priority', value: '#3 in the Q3 FY26 priority list', note: 'Agreed and published 28 Oct. Not the plan.' },
    { label: 'Plan', value: 'In the Q3 FY26 plan of record', note: 'Of record since 30 Oct. Scheduled 5th.' },
    { label: 'Elsewhere', value: '2 more evaluations open', note: 'FY27 planning · Supplier scenarios' },
  ],
  attention: [
    { mark: '! Missing', tone: 'mustard', text: 'Effort has no value. The Q3 commitment ranking compares on it.', scope: 'Q3 FY26 · Platform track' },
    { mark: '✦ Proposal', tone: 'slate', text: 'AI suggests altitude 3 instead of 4. Unreviewed.', scope: 'This item' },
    { mark: '! Changed', tone: 'mustard', text: 'Two groups ranked version 3; the item is now version 4.', scope: 'Q3 FY26' },
  ] as AttentionLine[],
  whatItIs: [
    { label: 'Why it matters', text: 'CAPA-118 cannot close until a second source is qualified, and the 77X-MF BOM depends on it.', ai: 'Improve' },
    { label: 'Success measure', text: null, ai: 'Propose one' },
    {
      label: 'Categories',
      text: 'Commitment · altitude 4 · Portfolio — primary. Risk · altitude 4 — “A single-source valve failure can stop the Dallas line again.” Opportunity · altitude 3 — “Could a second supplier cut valve cost by 8% next year?”',
      ai: 'Suggest',
    },
    { label: 'Triage', text: 'Pursue, 4 Sep, Ray Delgado — so it is active. Defer makes it deferred; reject archives it. Both take it off the item list.', ai: null },
  ] as { label: string; text: string | null; ai: string | null }[],
  assessment: [
    {
      attribute: 'Reach',
      accepted: '8',
      scale: '1–10, agreed',
      basis: 'No evidence linked',
      basisTone: 'mustard',
      usedIn: 'Q3 commitment ranking · Supply risk deep-dive',
      details: [
        { label: 'Scale', text: '1–10. 8 means “every line in two plants”. Set by the Platform track, inherited by its sessions.' },
        { label: 'Who set it', text: 'Proposed by Ana Ruiz, accepted by Ray Delgado, 6 Sep. A different value raises a change request.' },
        { label: 'Contributions · 5', text: '2 · 0, 4 · 1, 6 · 3, 8 · 1 — contested. Your contribution: 6.' },
        { label: 'Frozen copies', text: 'The Q3 commitment ranking used 8 (frozen 25 Oct). If this changes, that ranking still shows 8.' },
      ],
    },
    {
      attribute: 'Effort (score)',
      accepted: null,
      scale: '1–5, agreed',
      basis: 'Not assessed',
      basisTone: 'neutral',
      usedIn: 'Q3 commitment ranking',
      note: 'An effort score for comparing items. It is not the item’s effort in capacity units, and neither fills in the other.',
    },
    { attribute: 'Item effort', accepted: '75 CU', scale: 'capacity units', basis: 'Derived from work size L (9–26 weeks)', basisTone: 'neutral', usedIn: 'Planning only' },
    { attribute: 'Risk reduction', accepted: '9', scale: '1–10, agreed', basis: '2 sources', basisTone: 'green', usedIn: 'Supply risk deep-dive' },
    { attribute: 'Work type', accepted: 'Qualification', scale: 'list', basis: 'Set by hand', basisTone: 'neutral', usedIn: '—' },
    { attribute: 'Launch type', accepted: null, scale: 'list', basis: 'Not set', basisTone: 'mustard', usedIn: '—' },
  ] as AssessmentRow[],
  links: [
    { kind: 'Waits on (hard)', text: '77X-MF BOM revision — not started.', meaning: 'Blocks commitment, not ranking.', verdict: 'Open', tone: 'coral' },
    { kind: 'Competing', text: 'Single-source price concession.', meaning: 'Comparing them is fine; choosing both is the conflict. Settled in the Platform track.', verdict: 'Both above the line', tone: 'mustard' },
    { kind: 'Package', text: 'With “Life-test rig booking” — all or nothing.', meaning: 'Intact here; incomplete in the Q3 Engineering pool (flagged there).', verdict: 'Intact', tone: 'green' },
    { kind: 'Possible duplicate', text: '“Valve second-source” · undecided.', meaning: 'Matters where both are ranked: Q3 FY26.', verdict: 'Decide', tone: 'coral' },
    { kind: 'Part of', text: 'Supply resilience programme · altitude 5' },
    { kind: 'Parts', text: 'First article · life test · PPAP', verdict: '1 of 3 done', tone: 'neutral' },
  ] as LinkRow[],
  people: [
    { kind: 'Owner', text: 'Ray Delgado' },
    { kind: 'Item managers', text: '2 in the General queue' },
    { kind: 'Value approver', text: 'Ray Delgado (Engineering 2026 collection)' },
    { kind: 'Delivery team', text: 'Supplier quality' },
  ] as LinkRow[],
  cycles: [
    {
      name: 'Q3 FY26',
      standing: 'Priority #3 · in the plan',
      standingTone: 'mustard',
      tracks: [
        {
          name: 'Platform delivery',
          fit: 'takes Commitment × altitude 3–4 · fits',
          settings: [
            { setting: 'Item pool', value: 'Q3 Engineering · locked 20 Oct', source: 'set at track' },
            { setting: 'Criteria', value: 'Reach, Confidence, Effort score · set v2', source: 'from cycle' },
            { setting: 'Scales', value: '1–10 and 1–5', source: 'from workflow “Quarterly”' },
            { setting: 'Order changes', value: 'Allowed, with a reason', source: 'set at track' },
          ],
          sessions: [
            {
              name: 'Q3 commitment ranking',
              date: '25 Oct',
              verdict: 'Result 4 → ranking 3',
              tone: 'mustard',
              lines: [
                'Ranked version 3 (“Second valve supplier, PPAP by 30 Nov”). The item is now version 4: description reworded 2 Nov.',
                'Order changed by Maya Chen with a reason.',
              ],
            },
            {
              name: 'Supply risk deep-dive',
              date: '18 Sep',
              verdict: 'Result 1 of 9 · draft',
              tone: 'slate',
              settings: [{ setting: 'Criteria', value: 'Reach, Risk reduction', source: 'set at session · allowed by track' }],
              lines: ['Ranked version 2. Different from the session above: flagged for the cycle owner.'],
              flagged: true,
            },
          ],
        },
      ],
      outcomes: [
        { label: 'Published track result', text: 'Q3 commitment ranking (rule: latest published)' },
        { label: 'Priority list', text: '#3 of 8 above the cut · agreed and published 28 Oct' },
        { label: 'Plan of record', text: 'official 30 Oct · scheduled 5th (waits on BOM)' },
      ],
    },
    {
      name: 'FY27 planning',
      standing: 'Being considered',
      standingTone: 'slate',
      tracks: [
        {
          name: 'Cost',
          fit: 'takes Opportunity × altitude 3 · fits through its Opportunity category',
          settings: [
            { setting: 'Criteria', value: 'Cost impact, Confidence', source: 'from cycle' },
            { setting: 'Needs', value: 'Cost impact has no value', source: 'for this track', warn: true },
          ],
          sessions: [],
          emptySession: 'No session yet · next: 12 Nov',
        },
      ],
    },
  ] as CycleNode[],
  delivery: [
    { kind: 'Placement', text: 'Q3 FY26 plan of record · weeks 2–14' },
    { kind: 'Sequence vs rank', text: 'Ranked 3rd, scheduled 5th. Reason recorded: waits on the BOM revision.', verdict: 'Differs', tone: 'mustard' },
    { kind: 'Work packages', text: 'First article · Life test · PPAP' },
    { kind: 'Effort, capacity units', text: 'Planned 75 CU · actual 112 CU so far', verdict: '49% over', tone: 'mustard' },
    { kind: 'Impact avoided', text: 'One line stoppage avoided, $42,000 — linked back to Risk reduction' },
  ] as LinkRow[],
  versions: [
    { kind: 'v4 · 2 Nov', text: 'Description reworded.', meaning: 'Ray Delgado · “fixing wording only”', verdict: 'current', tone: 'neutral' },
    { kind: 'v3 · 21 Sep', text: 'Title and description rewritten.', meaning: 'Ranked by Q3 commitment ranking' },
    { kind: 'v2 · 2 Aug', text: 'Opportunity category added.', meaning: 'Ranked by Supply risk deep-dive' },
  ] as LinkRow[],
  actors: [
    { kind: '21 Sep', text: 'Altitude set to 4', meaning: 'person · Ray Delgado' },
    { kind: '26 Jul', text: 'Category Commitment', meaning: 'proposed by agent (classifier) · accepted by Ray Delgado', verdict: 'Accepted suggestion', tone: 'slate' },
    { kind: '25 Jul', text: 'Created from intake', meaning: 'drafted by agent · reviewed by Ray Delgado' },
  ] as LinkRow[],
};
